import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarBlank, CaretLeft, CaretRight, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import Nav from "@/components/Nav";
import { createBooking, fetchServices } from "@/lib/api";

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00",
  "13:00", "14:00", "15:00", "16:00",
];

const STEPS = [
  { id: 1, label: "Service" },
  { id: 2, label: "Schedule" },
  { id: 3, label: "Case Details" },
  { id: 4, label: "Contact" },
  { id: 5, label: "Review" },
];

const initial = {
  service: "",
  scheduled_date: null,
  scheduled_time: "",
  duration_hours: 4,
  location_type: "in_person",
  location_address: "",
  case_caption: "",
  case_number: "",
  deponent_name: "",
  jurisdiction: "",
  attorney_name: "",
  firm_name: "",
  contact_email: "",
  contact_phone: "",
  special_instructions: "",
};

const BookingPage = () => {
  const navigate = useNavigate();
  const { service: serviceParam } = useParams();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ ...initial, service: serviceParam || "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchServices().then(setServices).catch(() => setServices([]));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const selectedService = useMemo(
    () => services.find((s) => s.slug === form.service),
    [services, form.service]
  );

  const canProceed = () => {
    if (step === 1) return !!form.service;
    if (step === 2) return !!form.scheduled_date && !!form.scheduled_time && form.duration_hours > 0;
    if (step === 3) return !!form.case_caption && !!form.deponent_name && !!form.location_type;
    if (step === 4)
      return !!form.attorney_name && !!form.firm_name && !!form.contact_email && !!form.contact_phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        scheduled_date: format(form.scheduled_date, "yyyy-MM-dd"),
        duration_hours: Number(form.duration_hours),
      };
      const booking = await createBooking(payload);
      toast.success("Booking confirmed", { description: booking.reference });
      navigate(`/confirmation/${booking.reference}`);
    } catch (e) {
      toast.error("Could not submit booking", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Nav />

      {/* Heading */}
      <section className="px-6 md:px-12 lg:px-24 pt-16 pb-10 border-b border-border">
        <div className="eyebrow">Booking · Step {step} of {STEPS.length}</div>
        <h1 className="font-display text-4xl lg:text-5xl mt-4 text-ink-900 leading-tight">
          Reserve your videographer.
        </h1>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="step-tracker">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={cn(
                "flex items-center gap-3 px-4 py-3 border text-xs font-mono uppercase tracking-[0.18em] transition-colors",
                step === s.id
                  ? "bg-ink-900 text-white border-ink-900"
                  : step > s.id
                  ? "bg-paper-warm text-ink-900 border-ink-900"
                  : "bg-white text-muted-foreground border-border"
              )}
              data-testid={`step-${s.id}`}
            >
              <span>{String(s.id).padStart(2, "0")}</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Body */}
      <section className="px-6 md:px-12 lg:px-24 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          {step === 1 && (
            <Step1 services={services} value={form.service} onChange={(v) => set("service", v)} />
          )}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} />}
          {step === 4 && <Step4 form={form} set={set} />}
          {step === 5 && <Step5 form={form} service={selectedService} />}

          <div className="mt-12 flex items-center justify-between border-t border-border pt-8">
            <Button
              variant="ghost"
              onClick={() => (step === 1 ? navigate("/") : setStep(step - 1))}
              data-testid="prev-step-btn"
              className="rounded-none font-mono text-xs tracking-[0.2em] uppercase"
            >
              <CaretLeft size={16} weight="bold" className="mr-2" />
              {step === 1 ? "Cancel" : "Back"}
            </Button>

            {step < STEPS.length ? (
              <Button
                onClick={() => canProceed() && setStep(step + 1)}
                disabled={!canProceed()}
                data-testid="next-step-btn"
                className="rounded-none bg-ink-900 hover:bg-ink-800 text-white px-10 py-6 font-mono text-xs tracking-[0.2em] uppercase disabled:opacity-30"
              >
                Continue
                <CaretRight size={16} weight="bold" className="ml-2" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-booking-btn"
                className="rounded-none bg-ink-900 hover:bg-ink-800 text-white px-10 py-6 font-mono text-xs tracking-[0.2em] uppercase"
              >
                {submitting ? "Submitting…" : "Confirm booking →"}
              </Button>
            )}
          </div>
        </div>

        {/* Sidebar summary */}
        <aside className="lg:col-span-4">
          <div className="bg-paper-warm border border-border p-8 sticky top-28">
            <div className="eyebrow">Your booking</div>
            <div className="mt-6 space-y-5 text-sm">
              <SummaryRow label="Service" value={selectedService?.title || "—"} />
              <SummaryRow
                label="Date"
                value={form.scheduled_date ? format(form.scheduled_date, "EEE, MMM d, yyyy") : "—"}
              />
              <SummaryRow label="Time" value={form.scheduled_time || "—"} />
              <SummaryRow label="Duration" value={form.duration_hours ? `${form.duration_hours} h` : "—"} />
              <SummaryRow label="Location" value={form.location_type ? form.location_type.replace("_", " ") : "—"} />
              <SummaryRow label="Case" value={form.case_caption || "—"} />
              <SummaryRow label="Deponent" value={form.deponent_name || "—"} />
            </div>
            {selectedService && (
              <div className="mt-8 pt-6 border-t border-border">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">Lead time</div>
                <div className="font-display text-xl text-ink-900 mt-1">{selectedService.eta}</div>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  A coordinator will confirm pricing and final scheduling within one business hour. Our videographers
                  are also commissioned California Notaries.
                </p>
              </div>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground pt-1">{label}</span>
    <span className="text-right text-ink-900 capitalize">{value}</span>
  </div>
);

const Step1 = ({ services, value, onChange }) => (
  <div data-testid="step1-service-selection">
    <h2 className="font-display text-3xl text-ink-900 mb-2">Choose your service</h2>
    <p className="text-sm text-muted-foreground mb-8">Select the engagement that best matches your matter.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {services.map((s) => (
        <button
          key={s.slug}
          type="button"
          onClick={() => onChange(s.slug)}
          data-testid={`service-option-${s.slug}`}
          className={cn(
            "text-left p-6 border transition-all duration-300",
            value === s.slug
              ? "border-ink-900 bg-ink-900 text-white"
              : "border-border bg-white hover:border-ink-900"
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display text-xl">{s.title}</div>
              <p className={cn("text-sm mt-2 leading-relaxed", value === s.slug ? "text-white/70" : "text-muted-foreground")}>
                {s.summary}
              </p>
            </div>
            {value === s.slug && <CheckCircle size={22} weight="fill" className="text-champagne flex-shrink-0" />}
          </div>
          <div className="mt-6 pt-4 border-t border-border/40 flex justify-between items-center text-xs">
            <span className={cn("font-mono uppercase tracking-[0.18em]", value === s.slug ? "text-champagne" : "text-muted-foreground")}>
              Lead time
            </span>
            <span className={cn(value === s.slug ? "text-white/60" : "text-muted-foreground")}>{s.eta}</span>
          </div>
        </button>
      ))}
    </div>
  </div>
);

const Step2 = ({ form, set }) => (
  <div data-testid="step2-schedule">
    <h2 className="font-display text-3xl text-ink-900 mb-2">When?</h2>
    <p className="text-sm text-muted-foreground mb-8">Pick a date and time window. We confirm availability within an hour.</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Field label="Date">
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="date-picker-btn"
              className={cn(
                "w-full border border-input bg-white px-4 py-3 text-left flex items-center justify-between text-sm hover:border-ink-900 transition-colors",
                !form.scheduled_date && "text-muted-foreground"
              )}
            >
              {form.scheduled_date ? format(form.scheduled_date, "PPP") : "Choose a date"}
              <CalendarBlank size={18} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 rounded-none" align="start">
            <Calendar
              mode="single"
              selected={form.scheduled_date}
              onSelect={(d) => set("scheduled_date", d)}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
              data-testid="date-calendar"
            />
          </PopoverContent>
        </Popover>
      </Field>

      <Field label="Time">
        <Select value={form.scheduled_time} onValueChange={(v) => set("scheduled_time", v)}>
          <SelectTrigger data-testid="time-select" className="rounded-none border-input bg-white py-6">
            <SelectValue placeholder="Choose a time slot" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {TIME_SLOTS.map((t) => (
              <SelectItem key={t} value={t} data-testid={`time-option-${t}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Estimated duration (hours)">
        <Select value={String(form.duration_hours)} onValueChange={(v) => set("duration_hours", Number(v))}>
          <SelectTrigger data-testid="duration-select" className="rounded-none border-input bg-white py-6">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((h) => (
              <SelectItem key={h} value={String(h)} data-testid={`duration-option-${h}`}>
                {h} hour{h > 1 ? "s" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  </div>
);

const Step3 = ({ form, set }) => (
  <div data-testid="step3-case-details">
    <h2 className="font-display text-3xl text-ink-900 mb-2">Case details</h2>
    <p className="text-sm text-muted-foreground mb-8">All fields are confidential and used solely for coordination.</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Field label="Case caption *">
        <Input
          data-testid="case-caption-input"
          placeholder="e.g. Doe v. Acme Corp."
          value={form.case_caption}
          onChange={(e) => set("case_caption", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
      <Field label="Case / docket number">
        <Input
          data-testid="case-number-input"
          placeholder="e.g. 3:24-cv-04210"
          value={form.case_number}
          onChange={(e) => set("case_number", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
      <Field label="Deponent name *">
        <Input
          data-testid="deponent-name-input"
          placeholder="Full legal name"
          value={form.deponent_name}
          onChange={(e) => set("deponent_name", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
      <Field label="Jurisdiction">
        <Input
          data-testid="jurisdiction-input"
          placeholder="e.g. N.D. Cal."
          value={form.jurisdiction}
          onChange={(e) => set("jurisdiction", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
    </div>

    <div className="mt-8">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">Location *</div>
      <RadioGroup
        value={form.location_type}
        onValueChange={(v) => set("location_type", v)}
        className="grid grid-cols-1 md:grid-cols-3 gap-3"
        data-testid="location-type-radio"
      >
        {[
          { v: "in_person", l: "In person" },
          { v: "remote", l: "Remote / Zoom" },
          { v: "hybrid", l: "Hybrid" },
        ].map((o) => (
          <label
            key={o.v}
            className={cn(
              "flex items-center gap-3 px-5 py-4 border cursor-pointer transition-all",
              form.location_type === o.v ? "border-ink-900 bg-ink-900 text-white" : "border-border bg-white hover:border-ink-900"
            )}
            data-testid={`location-option-${o.v}`}
          >
            <RadioGroupItem value={o.v} className="rounded-none border-current" />
            <span className="text-sm">{o.l}</span>
          </label>
        ))}
      </RadioGroup>
    </div>

    {form.location_type !== "remote" && (
      <Field label="Location address" className="mt-8">
        <Input
          data-testid="location-address-input"
          placeholder="Office, court, or facility"
          value={form.location_address}
          onChange={(e) => set("location_address", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
    )}
  </div>
);

const Step4 = ({ form, set }) => (
  <div data-testid="step4-contact">
    <h2 className="font-display text-3xl text-ink-900 mb-2">Who&apos;s booking this?</h2>
    <p className="text-sm text-muted-foreground mb-8">We&apos;ll send the confirmation here.</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Field label="Attorney name *">
        <Input
          data-testid="attorney-name-input"
          value={form.attorney_name}
          onChange={(e) => set("attorney_name", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
      <Field label="Firm name *">
        <Input
          data-testid="firm-name-input"
          value={form.firm_name}
          onChange={(e) => set("firm_name", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
      <Field label="Email *">
        <Input
          data-testid="contact-email-input"
          type="email"
          value={form.contact_email}
          onChange={(e) => set("contact_email", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
      <Field label="Phone *">
        <Input
          data-testid="contact-phone-input"
          type="tel"
          value={form.contact_phone}
          onChange={(e) => set("contact_phone", e.target.value)}
          className="rounded-none py-6"
        />
      </Field>
    </div>

    <Field label="Special instructions" className="mt-8">
      <Textarea
        data-testid="special-instructions-input"
        rows={4}
        placeholder="Parking, building access, exhibits to be shared, etc."
        value={form.special_instructions}
        onChange={(e) => set("special_instructions", e.target.value)}
        className="rounded-none"
      />
    </Field>
  </div>
);

const Step5 = ({ form, service }) => (
  <div data-testid="step5-review">
    <h2 className="font-display text-3xl text-ink-900 mb-2">Review &amp; confirm</h2>
    <p className="text-sm text-muted-foreground mb-8">Verify the details below — a coordinator will email you to confirm.</p>

    <div className="border border-border divide-y divide-border">
      <ReviewRow label="Service" value={service?.title} />
      <ReviewRow label="Date" value={form.scheduled_date ? format(form.scheduled_date, "EEEE, MMMM d, yyyy") : ""} />
      <ReviewRow label="Time" value={`${form.scheduled_time} · ${form.duration_hours} hour(s)`} />
      <ReviewRow label="Location" value={`${form.location_type.replace("_", " ")}${form.location_address ? ` — ${form.location_address}` : ""}`} />
      <ReviewRow label="Case" value={`${form.case_caption}${form.case_number ? ` · ${form.case_number}` : ""}`} />
      <ReviewRow label="Deponent" value={form.deponent_name} />
      <ReviewRow label="Jurisdiction" value={form.jurisdiction || "—"} />
      <ReviewRow label="Attorney" value={`${form.attorney_name}, ${form.firm_name}`} />
      <ReviewRow label="Contact" value={`${form.contact_email} · ${form.contact_phone}`} />
      {form.special_instructions && <ReviewRow label="Notes" value={form.special_instructions} />}
    </div>
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3 block">
      {label}
    </Label>
    {children}
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="grid grid-cols-12 gap-4 p-5">
    <div className="col-span-4 font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground pt-1">{label}</div>
    <div className="col-span-8 text-ink-900 capitalize">{value || "—"}</div>
  </div>
);

export default BookingPage;
